// /src/components/Sidebar/index.tsx の完全なコード (ライフサイクル確認ログ追加版)
'use client'; // SidebarはClient Componentである必要があります

import Image from "next/image";
import styles from "./index.module.scss";
import { MyLink } from "@/components/MyLink";
import clsx from "clsx";
import { Post, PostDirectory } from "@/types/types";
import { useEffect, useState, useRef } from 'react';
import Script from "next/script";

// JSONデータのインポート (パスを確認)
import articleNewPostsJson from "public/posts_json/article_new_posts.json";
import articlePickupPostsJson from "public/posts_json/article_pickup_posts.json";
import caseNewPostsJson from "public/posts_json/case_new_posts.json";
import casePickupPostsJson from "public/posts_json/case_pickup_posts.json";
import newsNewPostsJson from "public/posts_json/news_new_posts.json";
import newsPickupPostsJson from "public/posts_json/news_pickup_posts.json";
import servicenewPostsJson from "public/posts_json/service_new_posts.json";
import servicePickupPostsJson from "public/posts_json/service_pickup_posts.json";

// 他のコンポーネントとユーティリティのインポート
import { SidebarTableOfContents } from "../SidebaTableOfContents";
import { createResizedImageUrl } from "@/utils/utils";

// --- グローバル型定義 ---
declare global {
  interface Window {
    googletag?: googletag.Googletag;
  }
  namespace googletag {
    interface Googletag {
      cmd: Array<() => void>;
      defineSlot(adUnitPath: string, size: googletag.SingleSizeArray | googletag.MultiSizeArray, div: string): googletag.Slot | null;
      display(div: string): void;
      enableServices(): void;
      pubads(): googletag.PubAdsService;
      destroySlots(slots?: googletag.Slot[]): boolean;
      apiReady?: boolean;
    }
    interface Slot {
        addService(service: googletag.Service): googletag.Slot;
        setTargeting(key: string, value: string | string[]): googletag.Slot;
    }
    interface PubAdsService {
        enableSingleRequest(): void;
        collapseEmptyDivs(): void;
        enableAsyncRendering(): void;
        refresh(slots?: googletag.Slot[]): void;
        setTargeting(key: string, value: string | string[]): googletag.PubAdsService;
        clearTargeting(key?: string): googletag.PubAdsService;
    }
    type Service = PubAdsService;
    type SingleSizeArray = [number, number];
    type MultiSizeArray = Array<SingleSizeArray | 'fluid'>;
  }
}
// --- グローバル型定義ここまで ---

// Heading型 (目次用)
interface Heading {
  level: string;
  title: string;
  href: string;
}

// SidebarコンポーネントのProps型定義
interface SidebarProps {
  headings?: Heading[];
  dir?: PostDirectory; // PostDirectory 型を使用
  adImage1?: string;
  adImage2?: string;
  urlSlug?: string;
  secondCate?: string;
}

// モジュールスコープでの広告関連定数
const adUnitPath1 = '/23284702477/ai_article_sidebar_vertical';
const adDivId1 = 'div-gpt-ad-1743216457219-0';
const adUnitPath2 = '/23284702477/ai_article_sidebar_rectangle';
const adDivId2 = 'div-gpt-ad-1743331777794-0';
const adSize: [number, number] = [300, 300];

// Sidebar Component
export const Sidebar = ({
  headings,
  dir = "article",
  adImage1,
  adImage2,
  urlSlug,
  secondCate,
}: SidebarProps) => {
  // ★★★ ライフサイクル確認ログ (Mount) ★★★
  console.log("[GAM Sidebar] Component Mounted");

  // State Hooks
  const [isHeadingOpen, setHeadingOpen] = useState(true);
  const [isPublicationOpen, setPublicationOpen] = useState(true);
  const [isCategoryOpen, setCategoryOpen] = useState(true);
  const [isRankingOpen, setRankingOpen] = useState(true);
  const [hasIpro, setHasIpro] = useState(false);

  // Refs for GAM
  const sidebarAdInitialized = useRef(false);
  const adSlotRefs = useRef<(googletag.Slot | null)[]>([null, null]);

  useEffect(() => {
    if (sidebarAdInitialized.current) {
      console.log("[GAM] 広告はすでに初期化済みです");
      return;
    }

    console.log("[GAM] 広告の初期化を開始します", {
      urlSlug,
      secondCate
    });

    if (typeof window === "undefined" || !window.googletag) {
      console.log("[GAM] googletag が利用できません");
      return;
    }

    const googletag = window.googletag;

    googletag.cmd.push(() => {
      try {
        // 共通設定を先に行う
        const pubads = googletag.pubads();
        pubads.enableSingleRequest();
        pubads.collapseEmptyDivs(); // 広告が表示されない場合にスペースを詰める
        googletag.enableServices();

        // スロット1の定義と設定
        console.log("[GAM] スロット1を定義します");
        const slot1 = googletag.defineSlot(
          adUnitPath1,
          adSize,
          adDivId1
        );

        if (slot1) {
          adSlotRefs.current[0] = slot1;
          slot1.addService(pubads);
          if (urlSlug) slot1.setTargeting("urlSlug", urlSlug);
          if (secondCate) slot1.setTargeting("SecondCate", secondCate);
          googletag.display(adDivId1);
        }

        // スロット2の定義と設定
        console.log("[GAM] スロット2を定義します");
        const slot2 = googletag.defineSlot(
          adUnitPath2,
          adSize,
          adDivId2
        );

        if (slot2) {
          adSlotRefs.current[1] = slot2;
          slot2.addService(pubads);
          if (urlSlug) slot2.setTargeting("urlSlug", urlSlug);
          if (secondCate) slot2.setTargeting("SecondCate", secondCate);
          googletag.display(adDivId2);
        }

        // 全スロットをリフレッシュ
        const slots = adSlotRefs.current.filter((slot): slot is googletag.Slot => slot !== null);
        if (slots.length > 0) {
          console.log("[GAM] スロットをリフレッシュします");
          pubads.refresh(slots);
        }

        sidebarAdInitialized.current = true;

      } catch (error) {
        console.error("[GAM] エラーが発生しました:", error);
        sidebarAdInitialized.current = false;
      }
    });

    return () => {
      if (!window.googletag) return;
      
      window.googletag.cmd.push(() => {
        const slots = adSlotRefs.current.filter((slot): slot is googletag.Slot => slot !== null);
        if (slots.length > 0) {
          console.log("[GAM] スロットを破棄します");
          if (window.googletag) {
            window.googletag.destroySlots(slots);
          }
        }
      });
      sidebarAdInitialized.current = false;
    };
  }, [urlSlug, secondCate]);

  // --- 記事データ取得ロジック ---
  const pickupPosts: Post[] = (() => {
    switch (dir) {
      case "article": return articlePickupPostsJson as Post[];
      case "case": return casePickupPostsJson as Post[];
      case "news": return newsPickupPostsJson as Post[];
      case "service": return servicePickupPostsJson as Post[];
      default: return [];
    }
  })();

  const newPosts: Post[] = (() => {
    switch (dir) {
      case "article": return articleNewPostsJson as Post[];
      case "case": return caseNewPostsJson as Post[];
      case "news": return newsNewPostsJson as Post[];
      case "service": return servicenewPostsJson as Post[];
      default: return [];
    }
  })();

  // --- JSXレンダリング ---
  return (
    <aside className={styles.sidebar}>
      {/* 1つ目の広告枠 */}
      <div 
        id={adDivId1}
        style={{
          minWidth: '300px',
          minHeight: '300px',
          margin: '0 auto',
          backgroundColor: process.env.NODE_ENV === 'development' ? '#f0f0f0' : 'transparent'
        }}
      >
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '12px',
            color: '#666',
            textAlign: 'center'
          }}>
            <p>広告スロット1: {adDivId1}</p>
            <p>サイズ: 300x300</p>
          </div>
        )}
      </div>

      {/* 広告枠とお問い合わせバナーの間のスペース */}
      <div style={{ marginBottom: '2rem' }}></div>

      {/* お問い合わせバナー */}
      <MyLink href="/contact" className={styles.banner2}>
        <Image
          src="https://aisouken.blob.core.windows.net/banner/footer/AIsouken_banner_contact.webp"
          alt="AI総合研究所　お問い合わせ"
          width={320}
          height={82}
          priority
        />
      </MyLink>

      {/* --- ピックアップ記事 --- */}
      <div className={styles.pickUp}>
        <p className={styles.pickUpText}>ピックアップ</p>
        <ul className={styles.pickUpList}>
          {pickupPosts?.map((post) => {
            const path = `/${dir}/${post.slug}`;
            const imgSrc =
              dir === "article" &&
              post.data.featureImg?.startsWith(
                "https://aisouken.blob.core.windows.net/article/"
              )
                ? createResizedImageUrl(post.data.featureImg, 400, 225)
                : post.data.featureImg;
            return (
              <li className={styles.pickUpListItem} key={post.slug}>
                <MyLink href={path} className={styles.pickUpLink}>
                  <div className={styles.pickUpLeft}>
                    <div className={styles.pickUpImageWrap}>
                      <Image
                        src={imgSrc || "/sample.png"}
                        alt={post.data.title || ""}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 96px"
                      />
                    </div>
                  </div>
                  <div className={styles.pickUpCard}>
                    <h2 className={styles.pickUpCardTitle}>
                      {post.data.title || ""}
                    </h2>
                    <div className={styles.pickUpCardDate}>
                      <Image src="/icon/clock-icon.svg" alt="" width={9} height={9}/>
                      <time dateTime={post.data.updateDate || post.data.date}>
                        {post.data.updateDate || post.data.date || ""}
                      </time>
                    </div>
                  </div>
                </MyLink>
              </li>
            );
          })}
        </ul>
      </div>

      {/* --- 資料ダウンロードバナー --- */}
      <MyLink href="/resources" className={styles.banner2}>
        <Image
          src="https://aisouken.blob.core.windows.net/banner/footer/AIsouken_banner_download.webp"
          alt="AI総合研究所　資料ダウンロード"
          width={320}
          height={82}
        />
      </MyLink>

      {/* --- 新着記事 --- */}
      <div className={clsx(styles.pickUp, styles.newItems)}>
        <p className={styles.pickUpText}>新着記事</p>
        <ul className={styles.pickUpList}>
          {newPosts?.map((post) => {
            const path = `/${dir}/${post.slug}`;
            const imgSrc =
              dir === "article" &&
              post.data.featureImg?.startsWith(
                "https://aisouken.blob.core.windows.net/article/"
              )
                ? createResizedImageUrl(post.data.featureImg, 400, 225)
                : post.data.featureImg;
            return (
              <li className={styles.pickUpListItem} key={post.slug}>
                <MyLink href={path} className={styles.pickUpLink}>
                  <div className={styles.pickUpLeft}>
                    <div className={styles.pickUpImageWrap}>
                      <Image
                        src={imgSrc || "/sample.png"}
                        alt={post.data.title || ""}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 96px"
                      />
                    </div>
                  </div>
                  <div className={styles.pickUpCard}>
                    <h2 className={styles.pickUpCardTitle}>
                      {post.data.title || ""}
                    </h2>
                    <div className={styles.pickUpCardDate}>
                      <Image src="/icon/clock-icon.svg" alt="" width={9} height={9}/>
                      <time dateTime={post.data.updateDate || post.data.date}>
                        {post.data.updateDate || post.data.date || ""}
                      </time>
                    </div>
                  </div>
                </MyLink>
              </li>
            );
          })}
        </ul>
      </div>

      {/* 2つ目の広告枠（AIMarketerバナーの代わりに配置） */}
      <div className={styles.stickyItem}>
        <div 
          id={adDivId2}
          style={{
            minWidth: '300px',
            minHeight: '300px',
            margin: '0 auto',
            backgroundColor: process.env.NODE_ENV === 'development' ? '#f0f0f0' : 'transparent'
          }}
        />

        {/* 目次 */}
        {headings && headings.length > 0 && <SidebarTableOfContents headings={headings} />}

        {/* --- i-PRO Section (Conditional) --- */}
        {hasIpro && (
          <div className={styles.ipro}>
            <Image src="/logo/ipro-logo.svg" alt="i-PRO株式会社" width={148} height={73} className={styles.iproLogo}/>
            <dl className={styles.iproList}>
              <div className={styles.iproListItem}><dt>社名</dt><dd>i-PRO株式会社</dd></div>
              {/* Add other i-pro details */}
            </dl>
           </div>
        )}
      </div>
    </aside>
  );
};